import React, { useState, useRef, useEffect } from "react";
import { Box, IconButton, Typography, Slider } from "@mui/material";
import { PlayArrow, Pause, Delete } from "@mui/icons-material";

const INPUT_BAR_STYLE = {
  display: "flex",
  alignItems: "center",
  height: 48,
  minHeight: 48,
  maxHeight: 48,
  padding: "0 14px",
  borderRadius: "24px",
  border: "1px solid",
  borderColor: "divider",
  backgroundColor: "background.paper",
  width: "100%",
  boxSizing: "border-box",
};

const formatTime = (seconds) => {
  if (seconds === undefined || Number.isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

/**
 * Preview de áudio gravado, integrado ao fluxo (estilo WhatsApp Web):
 * [Play/Pause] [Tempo / Duração] [Barra de progresso] [Excluir]
 */
const AudioPreview = ({ audioURL, onRemove }) => {
  const audioRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!audioURL || !audioRef.current) return;
    const el = audioRef.current;
    const onLoadedMetadata = () => setDuration(el.duration);
    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
    };
    el.addEventListener("loadedmetadata", onLoadedMetadata);
    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("ended", onEnded);
    if (el.duration && !Number.isNaN(el.duration)) setDuration(el.duration);
    return () => {
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("ended", onEnded);
    };
  }, [audioURL]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying(!playing);
  };

  const handleSeek = (_, value) => {
    if (!audioRef.current || duration <= 0) return;
    const t = Array.isArray(value) ? value[0] : value;
    const sec = (t / 100) * duration;
    audioRef.current.currentTime = sec;
    setCurrentTime(sec);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (!audioURL) return null;

  return (
    <Box sx={INPUT_BAR_STYLE}>
      <audio ref={audioRef} src={audioURL} preload="metadata" />
      <IconButton
        size="small"
        onClick={togglePlay}
        sx={{
          color: "primary.main",
          flexShrink: 0,
          "&:hover": { backgroundColor: "action.hover" },
        }}
        aria-label={playing ? "Pausar" : "Reproduzir"}
      >
        {playing ? <Pause sx={{ fontSize: 28 }} /> : <PlayArrow sx={{ fontSize: 28 }} />}
      </IconButton>
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: 0.25 }}>
        <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </Typography>
        <Slider
          size="small"
          value={progress}
          onChange={handleSeek}
          sx={{
            color: "primary.main",
            height: 4,
            py: 0,
            "& .MuiSlider-thumb": { width: 12, height: 12 },
          }}
          aria-label="Posição do áudio"
        />
      </Box>
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{
          color: "text.secondary",
          flexShrink: 0,
          "&:hover": { color: "error.main", backgroundColor: "action.hover" },
        }}
        aria-label="Excluir áudio"
      >
        <Delete />
      </IconButton>
    </Box>
  );
};

export default AudioPreview;
